/*
 * Black Friday campaign across the site: the bar at the top of every page, the label on
 * product cards, the banner, the tier bar's visibility and
 * the tier codes (snippets/campaign-*.liquid).
 *
 * The phase depends on the clock, not on when Shopify rendered or cached the page, so
 * the snippets render everything hidden and this script shows what fits the current
 * phase, using window.GB_CAMPAIGN (snippets/campaign-config.liquid):
 *
 * - before the early-access start: nothing;
 * - early access (early-access start until sale start, the 'teaser' phase): nothing on the
 *   site, no bar at the top either: early access lives on the early-access page alone. A
 *   visitor with early access (cookie gb_bf_early, set by the early-access page after the link in
 *   the email or a sign-up) sees 'early': the labels and the tier
 *   bar with the code;
 * - sale (sale start until sale end): the bar with every tier and its code and a
 *   countdown, the labels, the banner and the tier bar, for
 *   everyone;
 * - after the sale end: nothing, so the campaign switches itself off, tier bar included.
 *
 * Whenever the tier discount applies, the tier bar in the cart offers the code for the tier
 * the cart reaches, with a button that puts it on the cart: the visitor redeems the code
 * themselves, with one click instead of typing it at checkout. Shopping feeds may promote
 * the codes, which is why the sale bar names them on every page.
 *
 * Times come from assets/campaign-time.js and amounts from assets/cart-tier-bar.js,
 * both loaded first. The rules are pure functions on window.GbCampaignBar for
 * tests/unit/campaign-bar.test.js.
 */
(function (root) {
  "use strict";

  var DISMISS_KEY = "gb_campaign_closed";
  var EARLY_COOKIE = "gb_bf_early";

  /* 'teaser', 'sale' or null for a moment in time (UTC ms); a missing time skips its phase. */
  function campaignPhase(nowMs, teaserStart, saleStart, saleEnd) {
    if (saleStart !== null && saleEnd !== null && nowMs >= saleStart && nowMs < saleEnd)
      return "sale";

    if (teaserStart !== null && nowMs >= teaserStart && (saleStart === null || nowMs < saleStart))
      return "teaser";

    return null;
  }

  /* What this visitor sees: early access is 'early' for a visitor who has it. */
  function displayPhase(phase, hasEarlyAccess) {
    return phase === "teaser" && hasEarlyAccess ? "early" : phase;
  }

  /* The tier bar shows while the tier discount applies to this visitor. */
  function tierBarAllowed(display) {
    return display === "sale" || display === "early";
  }

  /*
   * The highest tier with a code that the subtotal reaches, or null. Tiers are
   * { min, discount, code } in the visitor's currency; a tier without a code is skipped.
   */
  function bestTier(subtotal, tiers) {
    var best = null;

    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].code && subtotal >= tiers[i].min && (!best || tiers[i].min > best.min))
        best = tiers[i];
    }

    return best;
  }

  function tierCode(subtotal, tiers) {
    var tier = bestTier(subtotal, tiers);

    return tier ? tier.code : null;
  }

  /*
   * What the code box in the cart says: 'offer' the code of the tier the cart reaches,
   * 'upgrade' when the cart carries a lower tier's code, 'applied' once it carries the
   * right one, 'other' when it carries another code (a Smile reward, a welcome code): the
   * tier codes combine with shipping discounts only, so ours would not add to it. Null for
   * an empty cart or below every tier. applied is the list of codes on the cart.
   */
  function codeBoxState(subtotal, count, applied, tiers) {
    if (!(count > 0)) return null;
    var tier = bestTier(subtotal, tiers);

    if (!tier) return null;

    var cart = {
      discount_codes: (applied || []).map(function (code) {
        return { code: code };
      }),
    };

    if (cartHasCode(cart, tier.code)) return { state: "applied", tier: tier };

    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].code && cartHasCode(cart, tiers[i].code))
        return { state: "upgrade", tier: tier };
    }

    if (cart.discount_codes.length > 0) return { state: "other", tier: tier };

    return { state: "offer", tier: tier };
  }

  /*
   * Whether the cart already carries the code, from cart.js's discount_codes (Shopify
   * stores codes upper-case). Null when the cart does not say: the caller falls back on
   * what it remembers having applied.
   */
  function cartHasCode(cart, code) {
    var codes = cart && cart.discount_codes;

    if (!Array.isArray(codes)) return null;
    var wanted = String(code).toUpperCase();

    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && String(codes[i].code).toUpperCase() === wanted) return true;
    }

    return false;
  }

  function hasEarlyCookie(cookieString) {
    return ("; " + String(cookieString || "")).indexOf("; " + EARLY_COOKIE + "=1") !== -1;
  }

  function earlyCookie(days, secure) {
    var maxAge = Math.max(1, Math.round(Number(days) || 14)) * 86400;

    return (
      EARLY_COOKIE + "=1; Max-Age=" + maxAge + "; Path=/; SameSite=Lax" + (secure ? "; Secure" : "")
    );
  }

  function pad(value) {
    return (value < 10 ? "0" : "") + value;
  }

  /* "2d 04:12:09", or "04:12:09" on the last day. */
  function countdownText(left, daysShort) {
    if (!left) return "";
    var clock = pad(left.hours) + ":" + pad(left.minutes) + ":" + pad(left.seconds);

    return left.days > 0 ? left.days + (daysShort || "d") + " " + clock : clock;
  }

  /* Closing the bar holds for this phase of this campaign only. */
  function dismissValue(phase, saleEnd) {
    return phase + "|" + (saleEnd || "");
  }

  /*
   * The sale sentence. With the tier discount it names the top percentage; without it,
   * the plain sentence.
   */
  function saleText(config, percent) {
    var texts = config.texts || {};
    var max = Number(config.maxDiscount);

    if (!(max > 0) || !percent) return texts.sale || "";

    return String(texts.saleAmount || "").replace("%DISCOUNT%", percent(max));
  }

  /*
   * The tiers with the minimum in the visitor's currency and their codes:
   * [{ min, discount, code }], discount in percent. Empty without a rate for that currency.
   */
  function visitorTiers(tiers, rate) {
    var list = [];

    if (!(rate > 0)) return list;

    for (var i = 0; i < (tiers || []).length; i++) {
      var tier = tiers[i];

      if (!(Number(tier[0]) >= 0) || !(Number(tier[1]) > 0)) continue;
      list.push({
        min: Math.round(Number(tier[0]) * rate),
        discount: Number(tier[1]),
        code: tier[2] || "",
      });
    }

    return list.sort(function (a, b) {
      return a.min - b.min;
    });
  }

  /* "10% korting vanaf €200", or "5% korting op alles" for a tier from 0. */
  function tierLabel(tier, texts, format, percent) {
    if (!(tier.min > 0))
      return String(texts.tierItemAll || "%DISCOUNT%").replace(
        "%DISCOUNT%",
        percent(tier.discount),
      );

    return String(texts.tierItem || "%DISCOUNT% / %MIN%")
      .replace("%DISCOUNT%", percent(tier.discount))
      .replace("%MIN%", format(tier.min));
  }

  /* Every tier with its code, for the sale bar: "5% korting op alles met code BF5 · …". */
  function codesText(tiers, texts, format, percent) {
    return tiers
      .map(function (tier) {
        var label = tierLabel(tier, texts, format, percent);

        if (!tier.code) return label;

        return String(texts.tierCode || "%TIER% (%CODE%)")
          .replace("%TIER%", label)
          .replace("%CODE%", tier.code);
      })
      .join(" · ");
  }

  var api = {
    campaignPhase: campaignPhase,
    displayPhase: displayPhase,
    tierBarAllowed: tierBarAllowed,
    tierCode: tierCode,
    codeBoxState: codeBoxState,
    cartHasCode: cartHasCode,
    hasEarlyCookie: hasEarlyCookie,
    earlyCookie: earlyCookie,
    countdownText: countdownText,
    dismissValue: dismissValue,
    saleText: saleText,
    visitorTiers: visitorTiers,
    tierLabel: tierLabel,
    codesText: codesText,
  };

  root.GbCampaignBar = api;

  if (typeof document === "undefined") return;

  var config = root.GB_CAMPAIGN;
  var time = root.GbCampaignTime;

  if (!config || !time) return;

  var teaserStart = time.parseAmsterdamTime(config.teaserStart);
  var saleStart = time.parseAmsterdamTime(config.saleStart);
  var saleEnd = time.parseAmsterdamTime(config.saleEnd);
  var texts = config.texts || {};
  var tierBar = root.GbTierBar;

  var rate = tierBar
    ? tierBar.currencyRate(
        config.currency,
        config.shopCurrency,
        root.Shopify && root.Shopify.currency && root.Shopify.currency.rate,
      )
    : null;

  var format =
    tierBar &&
    function (cents) {
      return tierBar.formatAmount(cents, config.locale, config.before, config.after);
    };

  var percent =
    tierBar &&
    function (value) {
      return tierBar.formatPercent(value, config.locale);
    };

  var sentence = saleText(config, percent);
  var tiers = visitorTiers(config.tiers, rate);
  var codes = format && percent && tiers.length ? codesText(tiers, texts, format, percent) : "";

  function readStorage(storage, key) {
    try {
      return root[storage].getItem(key);
    } catch {
      return null;
    }
  }

  function writeStorage(storage, key, value) {
    try {
      root[storage].setItem(key, value);
    } catch {
      /* Without storage the bar comes back, or the code is applied again: both harmless. */
    }
  }

  var dismissed = readStorage("localStorage", DISMISS_KEY);
  var currentDisplay;

  function display() {
    return displayPhase(
      campaignPhase(Date.now(), teaserStart, saleStart, saleEnd),
      hasEarlyCookie(document.cookie),
    );
  }

  /* Called by assets/cart-tier-bar.js before it draws. */
  api.showsTierBar = function () {
    return tierBarAllowed(currentDisplay);
  };

  /* Called by the early-access page once the visitor has access. */
  api.grantEarlyAccess = function (days) {
    document.cookie = earlyCookie(days, root.location.protocol === "https:");
    tick();
  };

  function fillTiers() {
    if (!format || !percent || !tiers.length) return;
    var top = 0;

    for (var t = 0; t < tiers.length; t++) top = Math.max(top, tiers[t].discount);
    var maxes = document.querySelectorAll("[data-campaign-max]:not([data-campaign-filled])");

    for (var m = 0; m < maxes.length; m++) {
      maxes[m].textContent = percent(top);
      maxes[m].setAttribute("data-campaign-filled", "");
      var wrap = maxes[m].closest("[data-campaign-max-wrap]");

      if (wrap) wrap.hidden = false;
    }

    var lists = document.querySelectorAll("[data-campaign-tiers]:not([data-campaign-filled])");

    for (var i = 0; i < lists.length; i++) {
      for (var j = 0; j < tiers.length; j++) {
        var item = document.createElement("li");
        item.textContent = tierLabel(tiers[j], texts, format, percent);

        /*
         * The code beside its tier, so the early-access page already says it works with a
         * code. A click copies it.
         */
        if (tiers[j].code) {
          var code = document.createElement("button");
          code.type = "button";
          code.className = "ea-tiers__code";
          code.textContent = tiers[j].code;
          code.setAttribute("data-copy-code", tiers[j].code);
          var copyLabel = lists[i].getAttribute("data-copy-label");

          if (copyLabel) code.setAttribute("aria-label", copyLabel + ": " + tiers[j].code);
          item.appendChild(code);
        }

        lists[i].appendChild(item);
      }

      lists[i].setAttribute("data-campaign-filled", "");
      lists[i].hidden = false;
    }
  }

  function apply(phase) {
    var shown = document.querySelectorAll("[data-campaign-only]");

    for (var i = 0; i < shown.length; i++) {
      var phases = (shown[i].getAttribute("data-campaign-only") || "").split(" ");
      shown[i].hidden = !phase || phases.indexOf(phase) === -1;
    }

    /* The reverse: what the campaign replaces while it shows, such as the regular sale badge. */
    var replaced = document.querySelectorAll("[data-campaign-except]");

    for (var r = 0; r < replaced.length; r++) {
      var except = (replaced[r].getAttribute("data-campaign-except") || "").split(" ");
      replaced[r].hidden = !!phase && except.indexOf(phase) !== -1;
    }

    var saleTexts = document.querySelectorAll("[data-campaign-sale-text]");

    for (var j = 0; j < saleTexts.length; j++) {
      if (sentence && saleTexts[j].textContent !== sentence) saleTexts[j].textContent = sentence;
    }

    /* Without the codes (no rate for this currency) the sale bar falls back on the sentence. */
    var codeTexts = document.querySelectorAll("[data-campaign-codes-text]");

    for (var c = 0; c < codeTexts.length; c++) {
      var line = codes || sentence;

      if (line && codeTexts[c].textContent !== line) codeTexts[c].textContent = line;
    }

    var bars = document.querySelectorAll("[data-campaign-bar]");

    for (var k = 0; k < bars.length; k++) {
      /* Only Black Friday has a bar: early access is never announced on the site. */
      bars[k].hidden = phase !== "sale" || dismissed === dismissValue(phase, config.saleEnd);
    }

    fillTiers();
    drawCodeBoxes();
  }

  /*
   * The code box in the cart (snippets/cart-tier-bar.liquid): the code of the tier the cart
   * reaches with a button that puts it on the cart through Shopify's /discount/<code>
   * route. Each code carries its own minimum, so a cart that drops below it gets the lower
   * tier's code offered again; checkout takes the code typed there too.
   */
  function drawCodeBoxes() {
    var boxes = document.querySelectorAll("[data-campaign-code-box]");

    for (var i = 0; i < boxes.length; i++) drawCodeBox(boxes[i]);
  }

  function drawCodeBox(box) {
    var applied = String(box.getAttribute("data-applied") || "")
      .split(",")
      .filter(Boolean);

    var result =
      tierBarAllowed(currentDisplay) && percent
        ? codeBoxState(
            Number(box.getAttribute("data-subtotal")) || 0,
            Number(box.getAttribute("data-count")) || 0,
            applied,
            tiers,
          )
        : null;

    box.hidden = !result;

    if (!result) return;

    var text = String(box.getAttribute("data-text-" + result.state) || "")
      .replace("%CODE%", result.tier.code)
      .replace("%DISCOUNT%", percent(result.tier.discount));

    var textEl = box.querySelector("[data-code-text]");

    if (textEl && textEl.textContent !== text && !box.hasAttribute("data-code-error"))
      textEl.textContent = text;
    box.classList.toggle("is-applied", result.state === "applied");
    var button = box.querySelector("[data-code-apply]");

    if (!button) return;
    button.hidden = result.state === "applied" || result.state === "other";
    button.setAttribute("data-code", result.tier.code);
  }

  /* A tier code on the early-access page: copy it and say so for a moment. */
  document.addEventListener("click", function (event) {
    var chip = event.target.closest && event.target.closest("[data-copy-code]");
    var clipboard = root.navigator && root.navigator.clipboard;

    if (!chip || !clipboard || !clipboard.writeText) return;
    var code = chip.getAttribute("data-copy-code");
    var list = chip.closest("[data-campaign-tiers]");
    var copied = (list && list.getAttribute("data-copied")) || code;
    clipboard.writeText(code).then(
      function () {
        chip.textContent = "\u2713 " + copied;
        chip.classList.add("is-copied");
        root.setTimeout(function () {
          chip.textContent = code;
          chip.classList.remove("is-copied");
        }, 1600);
      },
      function () {},
    );
  });

  document.addEventListener("click", function (event) {
    var button = event.target.closest && event.target.closest("[data-code-apply]");

    if (!button) return;
    var code = button.getAttribute("data-code");
    var box = button.closest("[data-campaign-code-box]");

    if (!code || !box) return;
    var base = config.root || "/";

    if (base.charAt(base.length - 1) !== "/") base += "/";
    button.disabled = true;
    root
      .fetch(
        base +
          "discount/" +
          encodeURIComponent(code) +
          "?redirect=" +
          encodeURIComponent(base + "cart.js"),
        {
          credentials: "same-origin",
        },
      )
      .then(function (response) {
        if (!response.ok) throw new Error("discount " + response.status);
        /* The drawer and the cart page redraw with the code, and this box says it is applied. */
        root.dispatchEvent(new CustomEvent("cart:refresh"));
      })
      .catch(function () {
        button.disabled = false;
        box.setAttribute("data-code-error", "");
        var textEl = box.querySelector("[data-code-text]");

        if (textEl)
          textEl.textContent = String(box.getAttribute("data-text-error") || "").replace(
            "%CODE%",
            code,
          );
      });
  });

  function tick() {
    var now = Date.now();
    var shown = display();

    if (shown !== currentDisplay) {
      currentDisplay = shown;
      apply(shown);

      if (tierBar && tierBar.redraw instanceof Function) tierBar.redraw();
    }

    var text = shown === "sale" ? countdownText(time.remaining(saleEnd, now), texts.daysShort) : "";
    var label = text ? String(texts.endsIn || "%TIME%").replace("%TIME%", text) : "";
    var counters = document.querySelectorAll("[data-campaign-countdown]");

    for (var i = 0; i < counters.length; i++) {
      if (counters[i].textContent !== label) counters[i].textContent = label;
    }
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest && event.target.closest("[data-campaign-close]");

    if (!button || !currentDisplay) return;
    dismissed = dismissValue(currentDisplay, config.saleEnd);
    writeStorage("localStorage", DISMISS_KEY, dismissed);
    apply(currentDisplay);
  });

  tick();
  root.setInterval(tick, 1000);

  /* Product cards arrive later on filtered collections and in sliders: show their labels too. */
  if (typeof MutationObserver !== "undefined") {
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      root.requestAnimationFrame(function () {
        scheduled = false;
        apply(currentDisplay);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})(typeof window !== "undefined" ? window : globalThis);
